import { z } from 'zod'
import { oc } from '@orpc/contract'

import { US_STATE_CODES } from '../../practitioner/handlers/member-credentials.contract'
import { ENCOUNTER_ID_PATTERN } from './call-signature.contract'

/**
 * The patient portal's own view of its visits.
 *
 * `getFutureAppointmentsByPatient` cannot serve this: it refuses any caller that is not a
 * Practitioner, because it exists for the call-center transfer flow and is org-scoped. These
 * three are the patient-facing counterparts — scoped to the caller's own record, and shaped for
 * what the portal renders rather than for what FHIR happens to store.
 */

/*
 * Everything here hangs off `/v2/patient/me/…`, and it has to.
 *
 * `GET /v2/patient/{id}` exists, so a one-segment sibling like `/v2/patient/visits` is matched by
 * it with `id = "visits"` — the request reaches the patient-read handler and dies as
 * "Patient/visits not found". `/v2/patient/me/portal-access` already dodges this the same way;
 * `me` also happens to say the thing these endpoints enforce, which is that they only ever answer
 * for the caller.
 */

/**
 * How many visits each half of the response carries. A cap, not a paging story — a patient with
 * more than this many visits ahead of them sees the soonest, which is what the screen is for.
 */
export const PATIENT_VISITS_PAGE_SIZE = 20

const visitSchema = z.object({
  encounterId: z.string(),
  appointmentId: z.string().nullable(),
  start: z.string().nullable(),
  end: z.string().nullable(),
  /** Why the visit was booked, as the reason picker recorded it. */
  reason: z.string().nullable(),
  practitionerName: z.string().nullable(),
  /**
   * The clinic that owns the visit. Consent and the authorization to release records are both
   * per-organization, so the portal cannot resolve either without this.
   */
  organizationId: z.string().nullable(),
  /** Whether the visit is a telehealth session, i.e. whether "Join visit" applies at all. */
  isVideoVisit: z.boolean(),
  /** Coverage the patient self-reported for this visit, for the readiness tiles. */
  insuranceCarrier: z.string().nullable(),
  policyMemberId: z.string().nullable(),
  /** A saved Coverage already attached to the appointment, if the patient picked one. */
  coverageId: z.string().nullable(),
  /** An uninsured visit paying the flat self-pay fee (PD-1943). */
  selfPay: z.boolean(),
  /** Where that fee stands, from the appointment's self-pay-status extension; null for insured visits. */
  selfPayStatus: z.enum(['unpaid', 'paid', 'refunded']).nullable(),
  visitState: z.string().nullable(),
  deviceCheckedAt: z.string().nullable(),
  /**
   * Whether the visit can still be cancelled — the same cutoff the cancel route enforces, so the
   * portal hides "Cancel" rather than offer a button the server will refuse.
   */
  canCancel: z.boolean(),
})

export const listPatientVisits = oc
  .route({ method: 'GET', path: '/v2/patient/me/visits' })
  .input(z.object({}))
  .output(
    z.object({
      upcoming: z.array(visitSchema),
      past: z.array(visitSchema),
    }),
  )

export const cancelPatientVisit = oc
  .route({ method: 'POST', path: '/v2/patient/me/visits/{encounterId}/cancel' })
  .input(z.object({ encounterId: z.string().min(1) }))
  .output(z.object({ success: z.boolean(), message: z.string().optional() }))

// A path or a dot-only id would make getCallAccess read another FHIR URL, so it refuses one with a
// BadRequestException this route would answer as a 500. Refusing it here makes it a 400, unread.
export const patientVisitCallAccessInputSchema = z.object({
  encounterId: z.string().regex(ENCOUNTER_ID_PATTERN),
})

/**
 * Whether the patient may join the video session, and under which session name.
 *
 * Deliberately narrower than the REST `GET /appointment/call/{encounterId}` it replaces: that
 * one also returns a consent `Bundle`, which is a FHIR resource at the boundary. The portal
 * generates its own signing documents through `consentGenerate` / `authorizationGenerate`, so
 * the flags are all it needs from here.
 */
export const patientVisitCallAccess = oc
  .route({ method: 'GET', path: '/v2/patient/me/visits/{encounterId}/call-access' })
  .input(patientVisitCallAccessInputSchema)
  .output(
    z.object({
      encounterId: z.string(),
      canJoin: z.boolean(),
      consentSigned: z.boolean(),
      authorizationSigned: z.boolean(),
      organizationId: z.string().nullable(),
      practitionerName: z.string().nullable(),
      sessionName: z.string().nullable(),
      /** Why joining is blocked, when it is. Safe to show the patient. */
      message: z.string().nullable(),
    }),
  )

export const savePatientVisitDeviceCheck = oc
  .route({ method: 'POST', path: '/v2/patient/me/visits/{encounterId}/device-check' })
  .input(z.object({ encounterId: z.string().min(1) }))
  .output(z.object({ encounterId: z.string(), deviceCheckedAt: z.string() }))

export const savePatientVisitLocation = oc
  .route({ method: 'POST', path: '/v2/patient/me/visits/{encounterId}/location' })
  .input(
    z.object({
      encounterId: z.string().min(1),
      state: z.enum(US_STATE_CODES),
    }),
  )
  .output(z.object({ encounterId: z.string(), visitState: z.string() }))
